import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useModal } from '../context/ModalContext';
import { useTheme } from '../context/ThemeContext';

const CATEGORY_LABELS = {
  en: { products: 'Product Launch', events: 'Events', awards: 'Awards', industry: 'Industry', press: 'Press Release' },
  ar: { products: 'إطلاق منتج', events: 'الفعاليات', awards: 'الجوائز', industry: 'الصناعة', press: 'بيان صحفي' },
};

const readingTime = (en, ar, lang) => {
  const text = lang === 'ar' ? ar : en;
  const words = text?.trim().split(/\s+/).length || 0;
  return Math.max(1, Math.ceil(words / 200));
};

const NewsCard = ({ article, featured = false }) => {
  const { language } = useLanguage();
  const { openModal } = useModal();
  const { isDark } = useTheme();
  const isRtl = language === 'ar';

  const title = language === 'ar' ? article.title_ar : article.title_en;
  const summary = language === 'ar' ? article.summary_ar : article.summary_en;
  const category = CATEGORY_LABELS[language]?.[article.category] || article.category;
  const readTime = readingTime(article.body_en || article.summary_en, article.body_ar || article.summary_ar, language);

  const formattedDate = article.published_at
    ? new Date(article.published_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-AE', {
        month: 'long', year: 'numeric',
      })
    : '';

  const handleClick = () => openModal('news-article', { slug: article.slug });

  return (
    <article
      onClick={handleClick}
      className={`group relative overflow-hidden rounded-sm cursor-pointer
        border transition-all duration-500
        ${isRtl ? 'text-right' : 'text-left'}
        ${featured
          ? 'min-h-[420px] lg:min-h-[480px]'
          : 'min-h-[260px] sm:min-h-[300px]'
        }
        ${isDark
          ? 'border-[rgba(212,175,55,0.1)] hover:border-[rgba(212,175,55,0.5)]'
          : 'border-gray-200 hover:border-[rgba(212,175,55,0.6)]'
        }
      `}
    >
      {/* Cover Image */}
      <div className="absolute inset-0">
        {article.cover_image_url ? (
          <img
            src={article.cover_image_url}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className={`w-full h-full ${isDark ? 'bg-[#181818]' : 'bg-gray-100'}`}>
            <div className="w-full h-full flex items-center justify-center opacity-10">
              <svg viewBox="0 0 80 60" className="w-24 h-24 fill-current text-[#D4AF37]">
                <path d="M40 5L5 55h70L40 5z" />
              </svg>
            </div>
          </div>
        )}
        {/* Gradient overlay */}
        <div className={`absolute inset-0 ${
          article.cover_image_url
            ? 'bg-gradient-to-t from-black/85 via-black/30 to-transparent'
            : isDark
              ? 'bg-gradient-to-t from-[#111111] via-[#111111]/60 to-transparent'
              : 'bg-gradient-to-t from-white/95 via-white/60 to-transparent'
        }`} />
      </div>

      {/* Gold border bottom line on hover */}
      <div className="edge-underline absolute bottom-0 h-0.5 w-0 bg-[#D4AF37] group-hover:w-full transition-all duration-500" />

      {/* Content */}
      <div className={`relative h-full flex flex-col justify-end p-5 ${featured ? 'p-6' : 'p-5'}`}>
        {/* Category badge */}
        <div className={`mb-3 flex flex-wrap items-center gap-2 ${isRtl ? 'flex-row-reverse justify-end' : ''}`}>
          <span className="inline-block px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest bg-[#D4AF37] text-black rounded-[2px]">
            {category}
          </span>
          {article.featured && (
            <span className="inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-[rgba(212,175,55,0.5)] text-[#D4AF37] rounded-[2px]">
              {language === 'ar' ? 'مميز' : 'Featured'}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className={`font-display font-bold leading-snug mb-2 transition-transform duration-300 group-hover:-translate-y-0.5
          ${article.cover_image_url ? 'text-white' : isDark ? 'text-white' : 'text-gray-900'}
          ${featured ? 'text-xl sm:text-2xl line-clamp-3' : 'text-base sm:text-lg line-clamp-2'}
        `}>
          {title}
        </h3>

        {/* Summary (featured only) */}
        {featured && (
          <p className={`text-sm line-clamp-2 mb-3 ${
            article.cover_image_url ? 'text-white/70' : isDark ? 'text-white/60' : 'text-gray-600'
          }`}>
            {summary}
          </p>
        )}

        {/* Meta */}
        <div className={`flex items-center gap-3 text-xs ${isRtl ? 'flex-row-reverse' : ''} ${
          article.cover_image_url ? 'text-white/50' : isDark ? 'text-white/40' : 'text-gray-400'
        }`}>
          <span>{formattedDate}</span>
          <span>·</span>
          <span>
            {readTime} {language === 'ar' ? 'دقائق' : 'min read'}
          </span>
          <span className={`${isRtl ? 'mr-auto' : 'ml-auto'} text-[#D4AF37] text-xs font-medium tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
            {language === 'ar' ? '← اقرأ المزيد' : 'Read More →'}
          </span>
        </div>
      </div>
    </article>
  );
};

export default NewsCard;
